import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { X, Minus, Plus, Trash2 } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { formatGBP } from "@/lib/format";
import PatternOverlay from "@/components/PatternOverlay";

export default function CartDrawer() {
  const { items, remove, setQty, totals, open, setOpen } = useCart();
  const nav = useNavigate();

  return (
    <>
      <div
        className={`fixed inset-0 bg-black/70 z-50 transition-opacity duration-500 ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        onClick={() => setOpen(false)}
        data-testid="cart-drawer-overlay"
      />
      <aside
        data-testid="cart-drawer"
        className={`pattern-bg fixed top-0 right-0 h-full w-full sm:w-[440px] bg-background border-l border-border z-50 transform transition-transform duration-500 flex flex-col ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <PatternOverlay opacity={items.length === 0 ? 0.13 : 0} size={520} />
        <div className="flex items-center justify-between px-6 py-5 border-b border-border">
          <div className="font-serif text-xl tracking-wider">Your Cart</div>
          <button
            onClick={() => setOpen(false)}
            data-testid="cart-close-btn"
            className="text-muted-foreground hover:text-primary"
          >
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {items.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center text-muted-foreground py-24 gap-4" data-testid="cart-empty">
              <div className="font-serif text-2xl text-foreground">Your cart is empty</div>
              <p className="text-sm">Compose your signature — begin with a scent.</p>
              <button
                onClick={() => {
                  setOpen(false);
                  nav("/shop");
                }}
                data-testid="cart-empty-shop-btn"
                className="mt-4 border border-border hover:border-primary hover:text-primary text-xs uppercase tracking-[0.22em] px-8 py-3"
              >
                Shop Fragrances
              </button>
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {items.map((i) => (
                <li key={i.product_id} className="py-5 flex gap-4" data-testid={`cart-item-${i.product_id}`}>
                  <div className="w-20 h-24 flex-none bg-secondary overflow-hidden">
                    <img src={i.image_url} alt={i.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-serif text-lg leading-tight">{i.name}</div>
                    <div className="text-xs uppercase tracking-widest text-muted-foreground mt-1">
                      {i.size_ml}ml
                    </div>
                    <div className="mt-3 flex items-center justify-between">
                      <div className="flex items-center border border-border">
                        <button
                          onClick={() => setQty(i.product_id, i.quantity - 1)}
                          className="p-1.5 text-muted-foreground hover:text-primary"
                          data-testid={`cart-qty-minus-${i.product_id}`}
                        >
                          <Minus size={12} />
                        </button>
                        <span className="w-8 text-center text-sm">{i.quantity}</span>
                        <button
                          onClick={() => setQty(i.product_id, i.quantity + 1)}
                          className="p-1.5 text-muted-foreground hover:text-primary"
                          data-testid={`cart-qty-plus-${i.product_id}`}
                        >
                          <Plus size={12} />
                        </button>
                      </div>
                      <div className="text-sm tracking-widest">
                        {formatGBP(i.price * i.quantity)}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => remove(i.product_id)}
                    className="text-muted-foreground hover:text-destructive"
                    data-testid={`cart-remove-${i.product_id}`}
                  >
                    <Trash2 size={16} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t border-border px-6 py-5 space-y-4">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Subtotal</span>
              <span data-testid="cart-subtotal">{formatGBP(totals.subtotal)}</span>
            </div>
            {totals.duoDiscount > 0 && (
              <div className="flex justify-between text-xs text-primary" data-testid="cart-duo-discount">
                <span>{totals.bundleTier === "trio" ? "Trio save (22%)" : "Duo save (15%)"}</span>
                <span>−{formatGBP(totals.duoDiscount)}</span>
              </div>
            )}
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>Shipping</span>
              <span>{totals.shipping === 0 ? "Free" : formatGBP(totals.shipping)}</span>
            </div>
            <div className="flex justify-between font-serif text-xl border-t border-border pt-4">
              <span>Total</span>
              <span data-testid="cart-total">{formatGBP(totals.total)}</span>
            </div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground text-right" data-testid="cart-vat-note">
              Includes VAT (20%) {formatGBP(totals.tax)}
            </div>
            <button
              onClick={() => {
                setOpen(false);
                nav("/checkout");
              }}
              data-testid="cart-checkout-btn"
              className="w-full bg-primary text-primary-foreground uppercase tracking-[0.22em] text-sm py-4 hover:opacity-90 transition-opacity"
            >
              Checkout
            </button>
            <Link
              to="/shop"
              onClick={() => setOpen(false)}
              className="block text-center text-xs uppercase tracking-[0.22em] text-muted-foreground hover:text-primary"
            >
              Continue Shopping
            </Link>
          </div>
        )}
      </aside>
    </>
  );
}
